import React, { useState, useEffect } from "react";
import { SalesPage as PageType, UserProfile } from "../../types/quiz";
import { startPolarCheckout } from "../../lib/polar";
import { trackEvent, trackPixel } from "../../utils/trackEvent";
import { useLang } from "../../i18n/LangContext";
import '../../assets/scss/quiz-editorial.scss';

interface Props {
  page: PageType;
  userProfile: UserProfile;
  onNext: () => void;
  quizId?: string;
}

// Render *asterisk*-wrapped spans as amber accent words. Keeps copy in plain
// JSON strings without HTML injection.
const renderAccented = (text: string) =>
  text.split(/\*([^*]+)\*/).map((chunk, i) =>
    i % 2 === 1 ? (
      <em key={i} className="value-line__accent">{chunk}</em>
    ) : (
      <React.Fragment key={i}>{chunk}</React.Fragment>
    ),
  );

const SalesPage: React.FC<Props> = ({ page, userProfile, quizId }) => {
  const { lang } = useLang();
  const activeQuizId = quizId || "doyourstep-quiz";
  const [selectedPlan, setSelectedPlan] = useState(page.defaultPlans[0]?.id);

  // Funnel: the offer/sales page was shown. Lets us see how many reach the
  // sale vs. how many go on to click checkout (drop-off visibility).
  useEffect(() => {
    trackEvent("offer_shown", { quizId: activeQuizId });
  }, [activeQuizId]);

  const handleCheckout = async () => {
    const plan = page.defaultPlans.find((p) => p.id === selectedPlan) || page.defaultPlans[0];
    if (!plan) return;

    // Funnel + Meta optimization: the user is heading to checkout.
    const priceValue = parseFloat((plan.price || "").replace(/[^0-9.]/g, "")) || undefined;
    trackEvent("checkout_initiated", {
      quizId: activeQuizId,
      email: userProfile.email || undefined,
      metadata: { planId: plan.id, planName: plan.period, source: "quiz_funnel" },
    });
    trackPixel("track", "InitiateCheckout", {
      value: priceValue,
      currency: "USD",
      content_name: plan.period,
    });

    try {
      await startPolarCheckout({
        email: userProfile.email || undefined,
        returnPath: `/subscription-success/${activeQuizId}`,
        metadata: {
          planId: plan.id,
          planName: plan.period,
          tags: JSON.stringify(plan.tags || []),
          quizId: activeQuizId,
          source: "quiz_funnel",
          userEmail: userProfile.email || "",
        },
      });
    } catch (error) {
      console.error("Polar checkout error:", error);
      alert(lang === "uk" ? "Не вдалося провести оплату. Спробуй ще раз." : "Payment failed. Please try again.");
    }
  };

  return (
    <div className="page page--sales">
      <div className="page__container">
        <h2 className="page__question">{page.title}</h2>
        {page.subtitle && <p className="page__subtitle">{page.subtitle}</p>}

        {/* Value block — the supportive middle */}
        {page.valueLines && page.valueLines.length > 0 && (
          <div className="value-block">
            {page.valueLines.map((line, i) => (
              <p key={i} className="value-line">{renderAccented(line)}</p>
            ))}
          </div>
        )}

        {/* What you get — concrete deliverables, shown openly (not hidden) */}
        {page.features && page.features.length > 0 && (
          <div className="sales-deliverables">
            <h3 className="sales-deliverables__title">
              {page.featuresLabel || (lang === "uk" ? "Що ти отримаєш" : "What you get")}
            </h3>
            <ul className="sales-deliverables__list">
              {page.features.map((feature, index) => (
                <li key={index} className="sales-deliverables__item">{feature}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Credibility line — the landing's authority, distilled */}
        {page.trust && (
          <p className="sales-trust">
            {page.trust.split(/\*([^*]+)\*/).map((chunk, i) =>
              i % 2 === 1 ? <strong key={i}>{chunk}</strong> : <React.Fragment key={i}>{chunk}</React.Fragment>,
            )}
          </p>
        )}

        {/* Plan(s) — calm single card */}
        <div className="pricing-plans">
          {page.defaultPlans.map((plan) => (
            <button
              key={plan.id}
              className={`pricing-plan ${selectedPlan === plan.id ? "pricing-plan--selected" : ""} ${
                plan.badge ? "pricing-plan--highlighted" : ""
              }`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.badge && <span className="pricing-plan__badge">{plan.badge}</span>}
              <div className="pricing-plan__price">
                {plan.oldPrice && <span className="pricing-plan__old-price">{plan.oldPrice}</span>}
                <span className="pricing-plan__current-price">{plan.price}</span>
              </div>
              {plan.subtitle && <p className="pricing-plan__subtitle">{plan.subtitle}</p>}
            </button>
          ))}
        </div>

        {/* CTA */}
        <button className="page__button page__button--primary" onClick={handleCheckout}>
          {page.buttonText || (lang === "uk" ? "Відкрити мій профіль" : "Open my profile")}
        </button>

        {/* Cancel-anytime reassurance */}
        {page.legal?.subscriptionText && (
          <p className="legal-text">{page.legal.subscriptionText}</p>
        )}

      </div>
    </div>
  );
};

export default SalesPage;
